
import { useState, useEffect } from "react";
import { getRequirementStatuses, updateRequirementStatus } from "@/integrations/supabase/requirementStatus";
import { Requirement, RequirementStatus } from "@/types/project";
import { toast } from "sonner";

export function useRequirementStatus(projectId: string | undefined) {
  const [requirementStatuses, setRequirementStatuses] = useState<RequirementStatus[]>([]);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchRequirementStatuses();
    }
  }, [projectId]);

  const fetchRequirementStatuses = async () => {
    if (!projectId) return;
    
    try {
      setIsStatusLoading(true);
      const statuses = await getRequirementStatuses(projectId);
      setRequirementStatuses(statuses);
    } catch (error) {
      console.error("Error fetching requirement statuses:", error);
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleStatusChange = async (
    requirement: Requirement, 
    status: 'Incomplete' | 'In Progress' | 'Not Applicable' | 'Ready to Submit'
  ) => {
    if (!projectId) return false;
    
    try {
      const reqId = requirement.requirement_id || requirement.id;
      const result = await updateRequirementStatus(projectId, reqId, status);
      
      if (result) {
        // Update the local state
        setRequirementStatuses(prev => {
          const existing = prev.find(s => s.requirement_id === reqId);
          if (existing) {
            return prev.map(s => s.requirement_id === reqId ? result : s);
          } else {
            return [...prev, result];
          }
        });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating requirement status:", error);
      toast.error("Failed to update requirement status");
      return false;
    }
  };

  // Helper function to get status for a requirement
  const getRequirementStatus = (requirement: Requirement): string => {
    const reqId = requirement.requirement_id || requirement.id;
    const statusRecord = requirementStatuses.find(s => s.requirement_id === reqId);
    return statusRecord?.status || "Incomplete";
  };

  return {
    requirementStatuses,
    isStatusLoading,
    getRequirementStatus,
    handleStatusChange,
    refreshStatuses: fetchRequirementStatuses
  };
}
